import React, { useEffect, useRef, useState } from "react"
import {
	Avatar,
	Checkbox,
	Chip,
	Grid,
	InputAdornment,
	List,
	ListItem,
	ListItemAvatar,
	ListItemIcon,
	ListItemText,
	TextField,
	Tooltip,
	Typography
} from "@material-ui/core"
import {
	FiberNew as NewChatIcon,
	ChatBubbleOutline as OnGoingChatIcon,
	Snooze as SnoozeChatIcon,
	Android as BotChatIcon,
	SettingsInputComponent as IntegrationChatIcon,
	Send as MessageBlastChatIcon,
	CheckCircleOutline as ArchivedChatIcon,
	Person as OwnAttendanceIcon,
	SupervisorAccount as OtherAttendantIcon,
	Search as SearchIcon,
	SearchOutlined as EmptyStateIcon,
	Group as GroupIcon,
	Check as CheckIcon,
	Lock as LockIcon,
	AddCircleOutline as NewAttendanceIcon,
	InfoOutlined as InfoIcon
} from "@material-ui/icons"
import { Loading } from "letalkui"

import { ActionDialog, Notification } from "@/components"

import ApiService from "@/services/Api"
import useSocket from "@/hooks/useSocket"
import useValidation, { ErrorType } from "@/hooks/useValidation"
import useDebounce from "@/hooks/useDebounce"
import { useChatGlobalStateStore } from "@/store/ChatGlobalState"
import { getAvatarSymbol } from "@/utils/string"
import { formatDateInBrazilianDate, formatDateInHours } from "@/utils/time"
import Messages from "@/utils/responseMessages/messages"

import { Message } from "@/protocols/chatGlobalStateProtocol"
import {
	ForwardMessageTargetResult,
	ForwardTarget,
	ForwardTargetAttendanceStatus,
	ForwardTargetsResponse,
	IChatStatus,
	MessageType
} from "@/protocols/channel"

import useStyles from "@/pages/Attendance/Chat/ConversationPanel/MessageList/CommonMessage/BaseMessage/ForwardMessageDialog/styles"

type ForwardMessageDialogProps = {
	isOpen: boolean
	message: Message
	onClose: () => void
}

type ForwardTargetDisabledReason = "waba-window-inactive" | "attended-by-another-user"

type AttendanceStatusChipLabel = "Novo" | "Em atendimento" | "Soneca" | "Bot" | "Integração" | "Envio em massa" | "Finalizado"

type ForwardTargetOption = {
	inboxChannelChatId: number
	title: string
	subtitle: string
	pictureUrl?: string
	phoneNumber?: string
	isGroup: boolean
	willOpenNewAttendance: boolean
	attendanceStatus: ForwardTargetAttendanceStatus
	disabledReason?: ForwardTargetDisabledReason
	attendanceStatusLabel: AttendanceStatusChipLabel
	attendanceStatusIcon: React.ReactElement
	attendanceOwnerLabel?: string
	attendanceOwnerIcon?: React.ReactElement
}

type AttendanceStatusChipConfig = {
	label: AttendanceStatusChipLabel
	icon: React.ReactElement
}

type ForwardableMediaMessageType = Extract<MessageType, "picture" | "video" | "audio" | "file">

type ForwardableMediaMessageLabel = "Imagem" | "Vídeo" | "Áudio" | "Documento"

const MAX_FORWARD_TARGETS_COUNT = 5
const ANOTHER_ATTENDANT_LABEL = "Com outro atendente"
const MIN_SEARCH_TEXT_LENGTH = 3
const SEARCH_DEBOUNCE_DELAY_IN_MS = 600
const MAX_VISIBLE_SELECTED_CHIPS_COUNT = 3
const NEW_ATTENDANCE_LABEL = "Abrirá novo atendimento"

const buildQuotedMessageText = (message: Message): string => {
	const messageTypeToLabel: Record<ForwardableMediaMessageType, ForwardableMediaMessageLabel> = {
		picture: "Imagem",
		video: "Vídeo",
		audio: "Áudio",
		file: "Documento"
	}

	const mediaLabel = messageTypeToLabel[message.type as ForwardableMediaMessageType]

	if (!mediaLabel) {
		return message.content
	}

	const mediaDescription = message.caption || message.fileName

	return mediaDescription ? `${mediaLabel} · ${mediaDescription}` : mediaLabel
}

const willOpenNewAttendance = (attendanceStatus: ForwardTargetAttendanceStatus): boolean => {
	const newAttendanceStatuses: ForwardTargetAttendanceStatus[] = ["finished", "none"]

	return newAttendanceStatuses.includes(attendanceStatus)
}

const buildAttendanceOwnerLabel = (forwardTarget: ForwardTarget): string | undefined => {
	const attendanceOwnershipToLabel: Record<ForwardTarget["attendanceOwnership"], string | undefined> = {
		self: "Com você",
		"another-user": forwardTarget.attendanceOwnerName ? `Com ${forwardTarget.attendanceOwnerName}` : ANOTHER_ATTENDANT_LABEL,
		none: undefined
	}

	return attendanceOwnershipToLabel[forwardTarget.attendanceOwnership]
}

const getDisabledReason = (forwardTarget: ForwardTarget, canForwardToOtherAttendantAttendances: boolean): ForwardTargetDisabledReason | undefined => {
	const isWabaWindowInactive = forwardTarget.isWabaWindowActive === false

	if (isWabaWindowInactive) {
		return "waba-window-inactive"
	}

	const isAttendedByAnotherUser = forwardTarget.attendanceOwnership === "another-user"
	const isAnotherUserAttendanceForbidden = isAttendedByAnotherUser && !canForwardToOtherAttendantAttendances

	if (isAnotherUserAttendanceForbidden) {
		return "attended-by-another-user"
	}

	return undefined
}

const buildOptionFromForwardTarget = (forwardTarget: ForwardTarget, canForwardToOtherAttendantAttendances: boolean): ForwardTargetOption => {
	const chatStatusToChip: Record<IChatStatus, AttendanceStatusChipConfig> = {
		queue: {
			label: "Novo",
			icon: <NewChatIcon />
		},
		"on-going": {
			label: "Em atendimento",
			icon: <OnGoingChatIcon />
		},
		snooze: {
			label: "Soneca",
			icon: <SnoozeChatIcon />
		},
		"chat-bot": {
			label: "Bot",
			icon: <BotChatIcon />
		},
		integration: {
			label: "Integração",
			icon: <IntegrationChatIcon />
		},
		"message-blast": {
			label: "Envio em massa",
			icon: <MessageBlastChatIcon />
		},
		archived: {
			label: "Finalizado",
			icon: <ArchivedChatIcon />
		}
	}

	const attendanceStatusChip = chatStatusToChip[forwardTarget.chatStatus] || chatStatusToChip.archived
	const isOwnAttendance = forwardTarget.attendanceOwnership === "self"
	const attendanceOwnerIcon = isOwnAttendance ? <OwnAttendanceIcon /> : <OtherAttendantIcon />
	const contactSubtitle = forwardTarget.isGroup ? "Grupo" : (forwardTarget.phoneNumber || "")

	return {
		inboxChannelChatId: forwardTarget.inboxChannelChatId,
		title: forwardTarget.nickname,
		subtitle: contactSubtitle,
		pictureUrl: forwardTarget.pictureUrl,
		phoneNumber: forwardTarget.phoneNumber,
		isGroup: Boolean(forwardTarget.isGroup),
		willOpenNewAttendance: willOpenNewAttendance(forwardTarget.attendanceStatus),
		attendanceStatus: forwardTarget.attendanceStatus,
		disabledReason: getDisabledReason(forwardTarget, canForwardToOtherAttendantAttendances),
		attendanceStatusLabel: attendanceStatusChip.label,
		attendanceStatusIcon: attendanceStatusChip.icon,
		attendanceOwnerLabel: buildAttendanceOwnerLabel(forwardTarget),
		attendanceOwnerIcon
	}
}

const isOptionMatchingFilter = (option: ForwardTargetOption, normalizedFilterText: string): boolean => {
	const normalizedTitle = option.title.toLowerCase()
	const isTitleMatching = normalizedTitle.includes(normalizedFilterText)

	const filterDigits = normalizedFilterText.replace(/\D/g, "")
	const hasFilterDigits = filterDigits.length > 0
	const phoneDigits = (option.phoneNumber || "").replace(/\D/g, "")
	const isPhoneMatching = hasFilterDigits && phoneDigits.includes(filterDigits)

	return isTitleMatching || isPhoneMatching
}

const ForwardMessageDialog: React.FC<ForwardMessageDialogProps> = (props) => {
	const {
		isOpen,
		message,
		onClose
	} = props

	const classes = useStyles()
	const socket = useSocket()
	const chatGlobalStateStore = useChatGlobalStateStore()
	const { triggerValidation } = useValidation()

	const [filterText, setFilterText] = useState("")
	const [selectedOptions, setSelectedOptions] = useState<ForwardTargetOption[]>([])
	const [isLoadingTargets, setIsLoadingTargets] = useState(false)
	const [targetsResponse, setTargetsResponse] = useState<ForwardTargetsResponse | null>(null)
	const [isLoadingSearch, setIsLoadingSearch] = useState(false)
	const [searchResponse, setSearchResponse] = useState<ForwardTargetsResponse | null>(null)

	const latestTargetsRequestIdRef = useRef(0)
	const latestSearchRequestIdRef = useRef(0)

	const inboxChannelId = message.inboxChannelId
	const channelType = chatGlobalStateStore.chat.current?.channelType
	const sourceInboxChannelChatId = chatGlobalStateStore.chat.current?.id

	const canForwardToOtherAttendantAttendances = Boolean(targetsResponse?.canForwardToOtherAttendantAttendances)
	const recentOptions = (targetsResponse?.recentTargets || []).map(forwardTarget => buildOptionFromForwardTarget(forwardTarget, canForwardToOtherAttendantAttendances))
	const lastAttendedOptions = (targetsResponse?.targets || []).map(forwardTarget => buildOptionFromForwardTarget(forwardTarget, canForwardToOtherAttendantAttendances))
	const searchText = filterText.trim()
	const isServerSearch = searchText.length >= MIN_SEARCH_TEXT_LENGTH
	const canForwardSearchToOtherAttendantAttendances = Boolean(searchResponse?.canForwardToOtherAttendantAttendances)
	const searchOptions = (searchResponse?.targets || []).map(forwardTarget => buildOptionFromForwardTarget(forwardTarget, canForwardSearchToOtherAttendantAttendances))
	const allOptions = [...recentOptions, ...lastAttendedOptions, ...searchOptions]

	const normalizedFilterText = searchText.toLowerCase()
	const hasFilterText = normalizedFilterText.length > 0
	const visibleRecentOptions = hasFilterText ? recentOptions.filter(option => isOptionMatchingFilter(option, normalizedFilterText)) : recentOptions
	const visibleLastAttendedOptions = hasFilterText ? lastAttendedOptions.filter(option => isOptionMatchingFilter(option, normalizedFilterText)) : lastAttendedOptions

	const hasTargets = allOptions.length > 0
	const canShowLocalSections = !isLoadingTargets && !isServerSearch
	const canShowRecentSection = canShowLocalSections && visibleRecentOptions.length > 0
	const canShowLastAttendedSection = canShowLocalSections && visibleLastAttendedOptions.length > 0
	const hasVisibleOptions = canShowRecentSection || canShowLastAttendedSection
	const canShowSearchResults = isServerSearch && !isLoadingSearch && searchOptions.length > 0
	const canShowSearchEmptyState = isServerSearch && !isLoadingSearch && Boolean(searchResponse) && searchOptions.length === 0
	const isLoadingAnyTargets = isServerSearch ? isLoadingSearch : isLoadingTargets
	const canShowEmptyState = canShowLocalSections && !hasVisibleOptions

	const selectedChatIds = selectedOptions.map(selectedOption => selectedOption.inboxChannelChatId)
	const selectedTargetsCount = selectedChatIds.length
	const visibleSelectedOptions = selectedOptions.slice(0, MAX_VISIBLE_SELECTED_CHIPS_COUNT)
	const hiddenSelectedOptions = selectedOptions.slice(MAX_VISIBLE_SELECTED_CHIPS_COUNT)
	const hasHiddenSelectedOptions = hiddenSelectedOptions.length > 0
	const hiddenSelectedOptionsTitles = hiddenSelectedOptions.map(selectedOption => selectedOption.title).join(", ")
	const hasSelectedTargets = selectedTargetsCount > 0
	const hasReachedTargetsLimit = selectedTargetsCount >= MAX_FORWARD_TARGETS_COUNT

	const isMessageSentByCompany = message.sentByCustomer
	const chatTitle = chatGlobalStateStore.chat.current?.title || ""
	const isGroupChat = chatGlobalStateStore.chat.current?.type === "group"
	const customerAuthorName = isGroupChat ? (message.senderName || chatTitle) : chatTitle
	const attendantAuthorName = message.senderName || "Você"
	const quotedMessageAuthor = isMessageSentByCompany ? attendantAuthorName : customerAuthorName
	const quotedMessageCreatedDate = new Date(message.createdAt)
	const quotedMessageDateText = `${formatDateInBrazilianDate(quotedMessageCreatedDate)} às ${formatDateInHours(quotedMessageCreatedDate)}`
	const quotedMessageText = buildQuotedMessageText(message)

	const loadForwardTargets = async () => {
		latestTargetsRequestIdRef.current += 1
		const requestId = latestTargetsRequestIdRef.current

		setIsLoadingTargets(true)

		try {
			const { data } = await ApiService.get<ForwardTargetsResponse>("/inbox-channel/forward-targets", {
				params: {
					inbox_channel_id: inboxChannelId,
					source_inbox_channel_chat_id: sourceInboxChannelChatId
				}
			})

			const isLatestRequest = requestId === latestTargetsRequestIdRef.current

			if (isLatestRequest) {
				setTargetsResponse(data)
			}
		} catch (error) {
			triggerValidation(error as ErrorType)
		}

		const isLatestRequest = requestId === latestTargetsRequestIdRef.current

		if (isLatestRequest) {
			setIsLoadingTargets(false)
		}
	}

	const loadSearchTargets = async () => {
		latestSearchRequestIdRef.current += 1
		const requestId = latestSearchRequestIdRef.current

		if (!isServerSearch) {
			setSearchResponse(null)
			setIsLoadingSearch(false)
			return
		}

		try {
			const { data } = await ApiService.get<ForwardTargetsResponse>("/inbox-channel/forward-targets", {
				params: {
					inbox_channel_id: inboxChannelId,
					source_inbox_channel_chat_id: sourceInboxChannelChatId,
					search: searchText
				}
			})

			const isLatestRequest = requestId === latestSearchRequestIdRef.current

			if (isLatestRequest) {
				setSearchResponse(data)
			}
		} catch (error) {
			triggerValidation(error as ErrorType)
		}

		const isLatestRequest = requestId === latestSearchRequestIdRef.current

		if (isLatestRequest) {
			setIsLoadingSearch(false)
		}
	}

	useDebounce(loadSearchTargets, searchText, SEARCH_DEBOUNCE_DELAY_IN_MS)

	const resetDialogState = () => {
		setSearchResponse(null)
		setFilterText("")
		setSelectedOptions([])
	}

	useEffect(() => {
		if (isOpen) {
			resetDialogState()
			loadForwardTargets()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen])

	const handleClose = () => {
		resetDialogState()
		onClose()
	}

	const handleFilterTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const nextSearchText = event.target.value.trim()
		const willSearchOnServer = nextSearchText.length >= MIN_SEARCH_TEXT_LENGTH

		setIsLoadingSearch(willSearchOnServer)
		setFilterText(event.target.value)
	}

	const handleToggleOption = (option: ForwardTargetOption) => {
		const isAlreadySelected = selectedChatIds.includes(option.inboxChannelChatId)

		if (!isAlreadySelected && hasReachedTargetsLimit) {
			return
		}

		const nextSelectedOptions = isAlreadySelected ? (
			selectedOptions.filter(selectedOption => selectedOption.inboxChannelChatId !== option.inboxChannelChatId)
		) : (
			[...selectedOptions, option]
		)

		setSelectedOptions(nextSelectedOptions)
	}

	const handleForwardError = (error: ErrorType) => {
		const errorCode = error?.body?.errorCode

		if (errorCode) {
			Notification.error({ message: Messages(errorCode) })
		} else {
			triggerValidation(error)
		}
	}

	const findOptionTitle = (inboxChannelChatId: number): string => {
		const matchingOption = selectedOptions.find(option => option.inboxChannelChatId === inboxChannelChatId)

		return matchingOption?.title || "Contato"
	}

	const buildFailedResultDescription = (result: ForwardMessageTargetResult): string => {
		const targetTitle = findOptionTitle(result.target.inboxChannelChatId)
		const failureReason = Messages(result.errorCode || "forward_message_failed")

		return `${targetTitle}: ${failureReason}`
	}

	const buildSuccessMessage = (sentResults: ForwardMessageTargetResult[]): string => {
		const [firstSentResult] = sentResults
		const firstTargetTitle = findOptionTitle(firstSentResult.target.inboxChannelChatId)
		const otherSentTargetsCount = sentResults.length - 1

		if (otherSentTargetsCount <= 0) {
			return `Mensagem encaminhada para ${firstTargetTitle}`
		}

		const otherTargetsText = otherSentTargetsCount === 1 ? "contato" : "contatos"

		return `Mensagem encaminhada para ${firstTargetTitle} e mais ${otherSentTargetsCount} ${otherTargetsText}`
	}

	const handleForward = async () => {
		if (!channelType || !hasSelectedTargets) {
			return
		}

		const selectedTargets = selectedChatIds.map(inboxChannelChatId => ({ inboxChannelChatId }))

		handleClose()

		try {
			const forwardMessageResponse = await socket.forwardMessage({
				channelType,
				inboxChannelId,
				messageId: message.id,
				targets: selectedTargets
			})

			const sentResults = forwardMessageResponse.results.filter(result => result.status === "sent")
			const failedResults = forwardMessageResponse.results.filter(result => result.status === "failed")
			const hasSentResults = sentResults.length > 0
			const hasFailedResults = failedResults.length > 0

			if (hasSentResults) {
				Notification.success({ message: buildSuccessMessage(sentResults) })
			}

			if (hasFailedResults) {
				const failedResultDescriptions = failedResults.map(buildFailedResultDescription)
				const failedResultsMessage = failedResultDescriptions.join(" | ")

				Notification.error({ message: `Não foi possível encaminhar para ${failedResultsMessage}` })
			}
		} catch (error) {
			handleForwardError(error as ErrorType)
		}
	}

	const disabledReasonToTooltipText: Record<ForwardTargetDisabledReason, string> = {
		"waba-window-inactive": "Janela de comunicação inativa",
		"attended-by-another-user": "Em atendimento com outro atendente — você não tem permissão para encaminhar"
	}

	const buildOptionState = (option: ForwardTargetOption) => {
		const isSelected = selectedChatIds.includes(option.inboxChannelChatId)
		const isBlockedByTarget = Boolean(option.disabledReason)
		const isBlockedByLimit = !isSelected && hasReachedTargetsLimit
		const isDisabled = isBlockedByTarget || isBlockedByLimit
		const tooltipText = option.disabledReason ? `${option.title} · ${disabledReasonToTooltipText[option.disabledReason]}` : option.title

		return { isSelected, isDisabled, isBlockedByTarget, tooltipText }
	}

	const avatarPastelClassNames = [
		classes.avatarPurple,
		classes.avatarBlue,
		classes.avatarGreen,
		classes.avatarYellow,
		classes.avatarRed,
		classes.avatarBeige
	]

	const renderOptionAvatar = (option: ForwardTargetOption, avatarClassName: string) => {
		if (option.isGroup) {
			return (
				<Avatar src={option.pictureUrl} className={`${avatarClassName} ${classes.groupAvatar}`}>
					<GroupIcon />
				</Avatar>
			)
		}

		return (
			<Avatar src={option.pictureUrl} className={`${avatarClassName} ${avatarPastelClassNames[option.inboxChannelChatId % avatarPastelClassNames.length]}`}>
				{getAvatarSymbol(option.title)}
			</Avatar>
		)
	}

	const selectedNewAttendanceCount = selectedOptions.filter(selectedOption => selectedOption.willOpenNewAttendance).length
	const hasSelectedNewAttendance = selectedNewAttendanceCount > 0
	const newAttendanceContactsText = selectedNewAttendanceCount === 1 ? "contato" : "contatos"

	const renderRecentOption = (option: ForwardTargetOption) => {
		const { isSelected, isDisabled, isBlockedByTarget, tooltipText } = buildOptionState(option)
		const recentItemClassName = isDisabled ? `${classes.recentItem} ${classes.recentItemDisabled}` : classes.recentItem
		const recentAvatarClassName = isSelected ? `${classes.recentAvatar} ${classes.recentAvatarSelected}` : classes.recentAvatar
		const recentStatusLabels = [option.attendanceStatusLabel, option.attendanceOwnerLabel, option.willOpenNewAttendance ? NEW_ATTENDANCE_LABEL : undefined]
		const recentStatusText = recentStatusLabels.filter(Boolean).join(" · ")
		const recentTooltipText = isBlockedByTarget ? tooltipText : `${tooltipText} · ${recentStatusText}`

		const handleRecentOptionKeyDown = (event: React.KeyboardEvent) => {
			const isToggleKey = event.key === "Enter" || event.key === " "

			if (isToggleKey && !isDisabled) {
				event.preventDefault()
				handleToggleOption(option)
			}
		}

		return (
			<Tooltip key={option.inboxChannelChatId} title={recentTooltipText}>
				<Grid
					className={recentItemClassName}
					role="button"
					tabIndex={isDisabled ? -1 : 0}
					aria-pressed={isSelected}
					aria-disabled={isDisabled}
					aria-label={recentTooltipText}
					onClick={() => !isDisabled && handleToggleOption(option)}
					onKeyDown={handleRecentOptionKeyDown}
				>
					<Grid className={classes.recentAvatarWrapper}>
						{renderOptionAvatar(option, recentAvatarClassName)}
						{isBlockedByTarget && (
							<Grid className={`${classes.recentCornerBadge} ${classes.recentLockBadge}`}>
								<LockIcon />
							</Grid>
						)}
						{isSelected && (
							<Grid className={`${classes.recentCornerBadge} ${classes.recentCheckBadge}`}>
								<CheckIcon />
							</Grid>
						)}
					</Grid>
					<Typography className={classes.recentName} noWrap>
						{option.title}
					</Typography>
				</Grid>
			</Tooltip>
		)
	}

	const renderOption = (option: ForwardTargetOption) => {
		const { isSelected, isDisabled, tooltipText } = buildOptionState(option)

		return (
			<Tooltip key={option.inboxChannelChatId} title={tooltipText}>
				<Grid>
					<ListItem
						button
						disabled={isDisabled}
						className={classes.targetRow}
						onClick={() => handleToggleOption(option)}
					>
						<ListItemIcon>
							<Checkbox
								edge="start"
								color="primary"
								checked={isSelected}
								disableRipple
							/>
						</ListItemIcon>
						<ListItemAvatar>
							{renderOptionAvatar(option, classes.avatar)}
						</ListItemAvatar>
						<ListItemText
							primary={option.title}
							secondary={option.subtitle}
							classes={{ primary: classes.optionTitle, secondary: classes.optionSubtitle }}
						/>
						<Grid container spacing={1} justifyContent="flex-end" wrap="nowrap" className={classes.attendanceChips}>
							<Grid item>
								<Chip
									size="small"
									icon={option.attendanceStatusIcon}
									label={option.attendanceStatusLabel}
									className={classes.attendanceChip}
								/>
							</Grid>
							{option.willOpenNewAttendance && (
								<Grid item>
									<Tooltip title="Ao encaminhar, um novo atendimento será aberto">
										<Chip
											size="small"
											icon={<NewAttendanceIcon />}
											label="Novo atendimento"
											className={`${classes.attendanceChip} ${classes.newAttendanceChip}`}
										/>
									</Tooltip>
								</Grid>
							)}
							{option.attendanceOwnerLabel && (
								<Grid item>
									<Chip
										size="small"
										icon={option.attendanceOwnerIcon}
										label={option.attendanceOwnerLabel}
										className={`${classes.attendanceChip} ${classes.ownerChip}`}
									/>
								</Grid>
							)}
						</Grid>
					</ListItem>
				</Grid>
			</Tooltip>
		)
	}

	const selectionScrollAreaClassName = hasSelectedNewAttendance ? classes.targetsScrollAreaWithSelectionAndBanner : classes.targetsScrollAreaWithSelection
	const targetsScrollAreaClassName = hasSelectedTargets ? `${classes.targetsScrollArea} ${selectionScrollAreaClassName}` : classes.targetsScrollArea

	const emptyStateTitle = hasTargets ? "Nenhum contato encontrado" : "Você ainda não atendeu ninguém neste canal"
	const emptyStateDescription = hasTargets ? "Confira a grafia do nome ou digite parte do telefone." : "Os contatos para os quais você encaminhou e os últimos 10 que você atendeu aparecem aqui."

	return (
		<ActionDialog
			title="Encaminhar mensagem"
			openDialog={isOpen}
			onClose={handleClose}
			onSave={handleForward}
			saveText={`Encaminhar (${selectedTargetsCount})`}
			saveButtonDisabled={!hasSelectedTargets}
			cancelText="Cancelar"
			dialogContentClassName={classes.dialogContent}
			fullWidth
		>
			<Grid container direction="column" spacing={2} wrap="nowrap">
				<Grid item>
					<Grid container direction="column" className={classes.quotedMessage}>
						<Grid item>
							<Typography variant="caption" className={classes.quotedMessageMeta}>
								{`${quotedMessageAuthor} · ${quotedMessageDateText}`}
							</Typography>
						</Grid>
						<Grid item>
							<Typography variant="body2" className={classes.quotedMessageText}>
								{quotedMessageText}
							</Typography>
						</Grid>
					</Grid>
				</Grid>

				<Grid item>
					<TextField
						value={filterText}
						onChange={handleFilterTextChange}
						placeholder="Nome, email e telefone"
						variant="outlined"
						size="small"
						fullWidth
						autoFocus
						InputProps={{
							className: classes.searchInput,
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon className={classes.searchIcon} />
								</InputAdornment>
							)
						}}
					/>
				</Grid>

				<Grid item>
					<Grid className={targetsScrollAreaClassName}>
						{isLoadingAnyTargets && (
							<Grid container justifyContent="center" className={classes.loadingContainer}>
								<Grid item>
									<Loading.FallbackSpinner isLoading={isLoadingAnyTargets}>
										<></>
									</Loading.FallbackSpinner>
								</Grid>
							</Grid>
						)}

						{canShowRecentSection && (
							<>
								<Typography className={classes.sectionTitle}>
									RECENTES
								</Typography>
								<Grid className={classes.recentGrid}>
									{visibleRecentOptions.map(renderRecentOption)}
								</Grid>
							</>
						)}

						{canShowLastAttendedSection && (
							<>
								<Typography className={classes.sectionTitle}>
									{`SEUS ÚLTIMOS ATENDIMENTOS · ${visibleLastAttendedOptions.length}`}
								</Typography>
								<List disablePadding>
									{visibleLastAttendedOptions.map(renderOption)}
								</List>
							</>
						)}

						{canShowSearchResults && (
							<>
								<Typography className={classes.sectionTitle}>
									{`RESULTADOS · ${searchOptions.length}`}
								</Typography>
								<List disablePadding>
									{searchOptions.map(renderOption)}
								</List>
							</>
						)}

						{canShowSearchEmptyState && (
							<List disablePadding>
								<ListItem>
									<ListItemIcon>
										<EmptyStateIcon className={classes.emptyStateIcon} />
									</ListItemIcon>
									<ListItemText
										primary={`Nenhum contato encontrado para "${searchText}"`}
										secondary="Confira a grafia ou busque por email ou telefone."
										classes={{ primary: classes.emptyStateText }}
									/>
								</ListItem>
							</List>
						)}

						{canShowEmptyState && (
							<List disablePadding>
								<ListItem>
									<ListItemIcon>
										<EmptyStateIcon className={classes.emptyStateIcon} />
									</ListItemIcon>
									<ListItemText
										primary={emptyStateTitle}
										secondary={emptyStateDescription}
										classes={{ primary: classes.emptyStateText }}
									/>
								</ListItem>
							</List>
						)}
					</Grid>
				</Grid>

				{hasSelectedTargets && (
					<Grid item>
						<Grid container wrap="nowrap" className={classes.selectedStrip}>
							{visibleSelectedOptions.map(selectedOption => (
								<Tooltip key={selectedOption.inboxChannelChatId} title={selectedOption.title}>
									<Chip
										size="small"
										avatar={renderOptionAvatar(selectedOption, classes.selectedChipAvatar)}
										label={selectedOption.title}
										onDelete={() => handleToggleOption(selectedOption)}
										className={classes.selectedChip}
									/>
								</Tooltip>
							))}
							{hasHiddenSelectedOptions && (
								<Tooltip title={hiddenSelectedOptionsTitles}>
									<Chip
										size="small"
										label={`+${hiddenSelectedOptions.length}`}
										className={classes.selectedMoreChip}
									/>
								</Tooltip>
							)}
						</Grid>
					</Grid>
				)}

				{hasSelectedNewAttendance && (
					<Grid item>
						<Grid container alignItems="center" wrap="nowrap" className={classes.newAttendanceBanner}>
							<InfoIcon className={classes.newAttendanceBannerIcon} />
							<Typography variant="body2">
								{`Ao encaminhar, um novo atendimento será aberto para ${selectedNewAttendanceCount} ${newAttendanceContactsText}.`}
							</Typography>
						</Grid>
					</Grid>
				)}

				<Grid item>
					<Typography variant="body2" align="right" className={classes.selectionCounter}>
						{`${selectedTargetsCount} de ${MAX_FORWARD_TARGETS_COUNT} selecionados`}
					</Typography>
				</Grid>
			</Grid>
		</ActionDialog>
	)
}

export default ForwardMessageDialog
